import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  GetStagesRequest,
  Stage,
} from "@/modules/administration/features/StageManagement/api/getStages/types";
import { getStages } from "@/modules/administration/features/StageManagement/api/getStages/getStagesApi";
import {
  CreateStageRequest,
  CreateStageResponse,
} from "@/modules/administration/features/StageManagement/api/createStage/types";
import { createStage } from "@/modules/administration/features/StageManagement/api/createStage/createStageApi";
import {
  UpdateStageRequest,
  UpdateStageResponse,
} from "@/modules/administration/features/StageManagement/api/updateStage/types";
import { updateStage } from "@/modules/administration/features/StageManagement/api/updateStage/updateStageApi";
import { deleteStage } from "@/modules/administration/features/StageManagement/api/deleteStage/deleteStageApi";

interface StageState {
  stages: Stage[];
  totalCount: number;
  loadingList: boolean;
  listError: string | null;

  loadingCreate: boolean;
  createError: string | null;

  loadingUpdate: boolean;
  updateError: string | null;

  loadingDelete: boolean;
  deleteError: string | null;
}

const initialState: StageState = {
  stages: [],
  totalCount: 0,
  loadingList: false,
  listError: null,

  loadingCreate: false,
  createError: null,

  loadingUpdate: false,
  updateError: null,

  loadingDelete: false,
  deleteError: null,
};

const stageSlice = createSlice({
  name: "stage",
  initialState,
  reducers: {
    setLoadingList(state, action: PayloadAction<boolean>) {
      state.loadingList = action.payload;
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    setStagesData(
      state,
      action: PayloadAction<{ stages: Stage[]; totalCount: number }>,
    ) {
      state.stages = action.payload.stages;
      state.totalCount = action.payload.totalCount;
    },

    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },

    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },

    setLoadingDelete(state, action: PayloadAction<boolean>) {
      state.loadingDelete = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
  },
});

export const {
  setLoadingList,
  setListError,
  setStagesData,
  setLoadingCreate,
  setCreateError,
  setLoadingUpdate,
  setUpdateError,
  setLoadingDelete,
  setDeleteError,
} = stageSlice.actions;

export default stageSlice.reducer;

export const getStagesAction =
  (
    params?: GetStagesRequest,
    onSuccess?: (stages: Stage[]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingList(true));
    dispatch(setListError(null));
    try {
      const response = await getStages(params);
      const stages = response.data;
      dispatch(
        setStagesData({
          stages,
          totalCount: response.meta?.totalCount ?? 0,
        }),
      );
      if (onSuccess) {
        onSuccess(stages);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch stages.";
      dispatch(setListError(message));
    } finally {
      dispatch(setLoadingList(false));
    }
  };

export const createStageAction =
  (
    requestData: CreateStageRequest,
    onSuccess?: (createdData: CreateStageResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));
    try {
      const response = await createStage(requestData);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create stage.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const updateStageAction =
  (
    uuid: string,
    requestData: UpdateStageRequest,
    onSuccess?: (updatedData: UpdateStageResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));
    try {
      const response = await updateStage(uuid, requestData);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update stage.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const deleteStageAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingDelete(true));
    dispatch(setDeleteError(null));
    try {
      await deleteStage(uuid);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete stage.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setLoadingDelete(false));
    }
  };
